import { useMemo, useState } from 'react';
import { t, Trans } from '@lingui/macro';

import Plus from '@decisiv/iconix/lib/components/Plus';

import Button from '@decisiv/ui-components/lib/components/Button';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import Loading from '@decisiv/ui-components/lib/components/Loading';

import * as ManagementLayout from 'components/ManagementLayout';

import useSort from 'utils/useSort';
import { useCurrentUserContext } from 'utils/User';
import PaginationFooter from 'components/PaginationFooter';

import SearchableFilter from 'components/SearchableFilter';
import { useManagedOemSegmentsQuery } from './hooks/useManagedSegmentsQuery';
import useOemSegmentsFilters from './hooks/useOemSegmentsFilters';
import { useManagementPageContext } from '../contexts/ManagementPageContext';
import { NoWrapHeaderTable } from '../components/styles';
import { generateColumns } from './columns';
import { ClearAllButton } from '../styles';

export type ManagedOemSegmentType = ExtractFromArray<
  ManagedOemSegmentsQuery['managedOemSegments']['collection']
>;

const OemSegments = () => {
  const { isSuperUser } = useCurrentUserContext();
  const [page, setPage] = useState(1);

  const {
    isFiltersDirty,
    pageParams,
    handleChangeOemSegmentsFilter,
    handleClearFilters,
  } = useManagementPageContext();

  const [{ sort }, { getSortProps }] = useSort();
  const { defaultSort, onSort } = getSortProps();

  const { data, loading } = useManagedOemSegmentsQuery({
    variables: {
      sort,
      page,
      filters: {
        ids: pageParams.oemSegmentIds,
      },
    },
  });

  const oemSegmentsFilterProps = useOemSegmentsFilters({
    value: pageParams.oemSegmentIds,
    onSubmit: handleChangeOemSegmentsFilter,
  });

  const columns = useMemo(() => generateColumns({ onSort }), [onSort]);

  const tableData = data?.managedOemSegments.collection ?? [];
  const pagination = data?.managedOemSegments.pagination;

  return (
    <ManagementLayout.TabBody.Container>
      <ManagementLayout.TabBody.Header.Container>
        <ManagementLayout.TabBody.Header.Left>
          {isSuperUser && (
            <Button
              disabled
              text={t`Create OEM Segment`}
              icon={Plus}
              size="small"
            />
          )}
        </ManagementLayout.TabBody.Header.Left>
        <ManagementLayout.TabBody.Header.Right>
          <P size="small" marginRight={1}>
            <Trans>Search Segments by Name:</Trans>
          </P>
          <SearchInput value="" hideLabel name="searchBySegment" size="small" />
        </ManagementLayout.TabBody.Header.Right>
      </ManagementLayout.TabBody.Header.Container>
      <ManagementLayout.TabBody.Content.Container>
        <ManagementLayout.TabBody.Content.Filters>
          <SearchableFilter
            popoverLabel={t`OEM Segments`}
            placement="bottom-start"
            {...oemSegmentsFilterProps}
          />
          {isFiltersDirty && (
            <ClearAllButton
              variant="ghost"
              onClick={handleClearFilters}
              text={t`Clear All`}
              size="small"
            />
          )}
        </ManagementLayout.TabBody.Content.Filters>
        {loading && (
          <Loading
            flex={1}
            justifyContent="center"
            size="large"
            title={t`Loading...`}
          />
        )}
        {!loading && (
          <NoWrapHeaderTable
            height="100%"
            scroll={['x', 'y']}
            data={tableData}
            columns={columns}
            onSort={onSort}
            defaultSort={defaultSort}
            footer={() => (
              <PaginationFooter
                // @ts-expect-error - JS file
                title={t`Oem Segments`}
                setCurrentPage={setPage}
                totalOnPage={tableData.length}
                page={pagination?.page}
                perPage={pagination?.perPage}
                totalEntries={pagination?.totalEntries}
                totalPages={pagination?.totalPages}
              />
            )}
          />
        )}
      </ManagementLayout.TabBody.Content.Container>
    </ManagementLayout.TabBody.Container>
  );
};

export default OemSegments;
